import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import moment from 'moment';
import { appointmentService } from '../../services/api';

const CoachScheduleSection = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('upcoming'); // upcoming | all | past

  const loadSchedule = async () => {
    setLoading(true);
    setError('');
    const res = await appointmentService.getAppointments();
    setLoading(false);
    if (res.success && res.data) {
      setAppointments(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } else {
      const errText = typeof res.message === 'string'
        ? res.message
        : (res.message?.message || 'Errore nel caricamento del calendario lezioni');
      setError(errText);
    }
  };

  useEffect(() => {
    loadSchedule();
  }, []);

  const now = moment();
  const filtered = appointments.filter((app) => {
    const end = moment(app.endTime);
    if (viewMode === 'upcoming') return end.isSameOrAfter(now);
    if (viewMode === 'past') return end.isBefore(now);
    return true;
  });

  return (
    <Card className="mb-4 shadow-sm border-0">
      <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center py-3">
        <h5 className="mb-0 fw-bold">
          <i className="fas fa-chalkboard-teacher me-2 text-warning"></i>
          Le Mie Lezioni Assegnate (Coach / Istruttore)
        </h5>
        <div className="btn-group btn-group-sm">
          <Button
            variant={viewMode === 'upcoming' ? 'warning' : 'outline-light'}
            onClick={() => setViewMode('upcoming')}
          >
            In Programma
          </Button>
          <Button
            variant={viewMode === 'past' ? 'warning' : 'outline-light'}
            onClick={() => setViewMode('past')}
          >
            Passate
          </Button>
          <Button
            variant={viewMode === 'all' ? 'warning' : 'outline-light'}
            onClick={() => setViewMode('all')}
          >
            Tutte
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-3">
        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="warning" size="sm" />
            <span className="ms-2 text-muted small">Caricamento delle lezioni...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="far fa-calendar-alt fa-2x mb-2 d-block text-secondary"></i>
            Nessuna lezione assegnata per il periodo selezionato.
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead className="table-light small">
                <tr>
                  <th>Data & Orario</th>
                  <th>Titolo Lezione</th>
                  <th>Stanza / Location</th>
                  <th>Gruppo</th>
                  <th className="text-center">Atleti Iscritti</th>
                  <th className="text-center">Stato</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((app) => {
                  let statusBadge = <Badge bg="primary">In Programma</Badge>;
                  if (app.status === 'completed') statusBadge = <Badge bg="success">Completata</Badge>;
                  if (app.status === 'cancelled') statusBadge = <Badge bg="secondary">Annullata</Badge>;

                  return (
                    <tr key={app.id}>
                      <td>
                        <div className="fw-semibold text-dark">
                          {moment(app.startTime).format('dddd DD MMMM YYYY')}
                        </div>
                        <small className="text-muted">
                          {moment(app.startTime).format('HH:mm')} - {moment(app.endTime).format('HH:mm')}
                        </small>
                      </td>
                      <td className="fw-bold text-primary">
                        {app.title}
                        {app.description && (
                          <small className="d-block text-muted fw-normal text-truncate" style={{ maxWidth: '250px' }}>
                            {app.description}
                          </small>
                        )}
                      </td>
                      <td>
                        <i className="fas fa-map-marker-alt text-danger me-1"></i>
                        {app.locationName}
                      </td>
                      <td>
                        {app.groupName ? (
                          <Badge bg="light" text="dark" className="border">
                            {app.groupName}
                          </Badge>
                        ) : (
                          <span className="text-muted small">Individuale</span>
                        )}
                      </td>
                      <td className="text-center">
                        <Badge bg="info" className="px-2 py-1">
                          <i className="fas fa-users me-1"></i>
                          {app.participantCount}
                        </Badge>
                      </td>
                      <td className="text-center">
                        {statusBadge}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default CoachScheduleSection;
